import React, { useState, useEffect } from 'react';
import CRUDList from '../Common/CRUDList';
import DateInput from '../Common/DateInput';
import { payments, bookings, tickets, ticketSegments, agents, carriers, currencies } from '../../services/api';
import { formatDate, formatAmount } from '../../utils/dateFormat';
import {
  paymentInitiators,
  paymentTypes,
  paymentDirections,
  paymentSubjectTypes,
  paymentSubjectSubtypes
} from '../../constants/travelEnums';

function PaymentForm({ formData, setFormData }) {
  const [bookingsList, setBookingsList] = useState([]);
  const [ticketsList, setTicketsList] = useState([]);
  const [segmentsList, setSegmentsList] = useState([]);
  const [agentsList, setAgentsList] = useState([]);
  const [carriersList, setCarriersList] = useState([]);
  const [currenciesList, setCurrenciesList] = useState([]);

  useEffect(() => {
    loadBookings();
    loadAgents();
    loadCarriers();
    loadCurrencies();
  }, []);

  // Load tickets when booking context changes (secondary, ticket booking, or root)
  useEffect(() => {
    const bookingForTickets = formData.payment_booking_id || formData.ticket_booking_id || formData.payment_root_booking_id;

    if (bookingForTickets) {
      loadTickets(bookingForTickets);
    } else {
      setTicketsList([]);
      setSegmentsList([]);
      setFormData(prev => ({ ...prev, payment_ticket_id: null, payment_tsegment_id: null }));
    }
  }, [formData.payment_booking_id, formData.payment_root_booking_id, formData.ticket_booking_id]);

  // Load segments when ticket changes
  useEffect(() => {
    if (formData.payment_ticket_id) {
      loadSegments(formData.payment_ticket_id);
    } else {
      setSegmentsList([]);
      setFormData(prev => ({ ...prev, payment_tsegment_id: null }));
    }
  }, [formData.payment_ticket_id]);

  const loadBookings = async () => {
    try {
      const response = await bookings.getAll();
      setBookingsList(response.data);
    } catch (err) {
      console.error('Failed to load bookings', err);
    }
  };

  const loadTickets = async (bookingId) => {
    try {
      const response = await tickets.getAll({ booking_id: bookingId });
      setTicketsList(response.data);
    } catch (err) {
      console.error('Failed to load tickets', err);
    }
  };

  const loadSegments = async (ticketId) => {
    try {
      const response = await ticketSegments.getAll({ ticket_id: ticketId });
      setSegmentsList(response.data);
    } catch (err) {
      console.error('Failed to load segments', err);
    }
  };

  const loadAgents = async () => {
    try {
      const response = await agents.getAll();
      setAgentsList(response.data);
    } catch (err) {
      console.error('Failed to load agents', err);
    }
  };

  const loadCarriers = async () => {
    try {
      const response = await carriers.getAll();
      setCarriersList(response.data);
    } catch (err) {
      console.error('Failed to load carriers', err);
    }
  };

  const loadCurrencies = async () => {
    try {
      const response = await currencies.getAll();
      setCurrenciesList(response.data);
    } catch (err) {
      console.error('Failed to load currencies', err);
    }
  };

  // Get root bookings (those without parent or are themselves parents)
  const rootBookings = bookingsList.filter(b => !b.booking_parent_id);

  // Get child bookings for selected root booking
  const childBookings = formData.payment_root_booking_id
    ? bookingsList.filter(b => b.booking_parent_id === formData.payment_root_booking_id)
    : [];

  return (
    <>
      <div className="form-group">
        <label>Payment Date *</label>
        <DateInput
          value={formData.payment_date || ''}
          onChange={(value) => setFormData({ ...formData, payment_date: value })}
          required
        />
      </div>

      <div className="form-group">
        <label>Root Booking *</label>
        <select
          value={formData.payment_root_booking_id || ''}
          onChange={(e) => {
            const rootId = e.target.value ? parseInt(e.target.value) : null;
            setFormData({
              ...formData,
              payment_root_booking_id: rootId,
              payment_booking_id: null,
              payment_ticket_id: null,
              payment_tsegment_id: null,
              ticket_booking_id: rootId
            });
          }}
          required
        >
          <option value="">Select root booking</option>
          {rootBookings.map(b => (
            <option key={b.booking_id} value={b.booking_id}>
              {b.booking_pnr} - {b.agent_name || b.carrier_name || 'Unknown'} ({b.booking_date})
            </option>
          ))}
        </select>
      </div>

      {formData.payment_root_booking_id && childBookings.length > 0 && (
        <div className="form-group">
          <label>Secondary Booking</label>
          <select
            value={formData.payment_booking_id || ''}
            onChange={(e) => {
              const bookingId = e.target.value ? parseInt(e.target.value) : null;
              setFormData({
                ...formData,
                payment_booking_id: bookingId,
                payment_ticket_id: null,
                payment_tsegment_id: null,
                ticket_booking_id: bookingId || formData.payment_root_booking_id
              });
            }}
          >
            <option value="">None (root booking only)</option>
            {childBookings.map(b => (
              <option key={b.booking_id} value={b.booking_id}>
                {b.booking_pnr} - {b.agent_name || b.carrier_name || 'Unknown'} ({b.booking_date})
              </option>
            ))}
          </select>
        </div>
      )}

      {(formData.payment_booking_id || formData.payment_root_booking_id || formData.ticket_booking_id) && ticketsList.length > 0 && (
        <div className="form-group">
          <label>Ticket</label>
          <select
            value={formData.payment_ticket_id || ''}
            onChange={(e) => {
              const ticketId = e.target.value ? parseInt(e.target.value) : null;
              setFormData({
                ...formData,
                payment_ticket_id: ticketId,
                payment_tsegment_id: null
              });
            }}
          >
            <option value="">None</option>
            {ticketsList.map(t => (
              <option key={t.ticket_id} value={t.ticket_id}>
                {t.ticket_number} - {t.passenger_name}
              </option>
            ))}
          </select>
        </div>
      )}

      {formData.payment_ticket_id && segmentsList.length > 0 && (
        <div className="form-group">
          <label>Ticket Segment</label>
          <select
            value={formData.payment_tsegment_id || ''}
            onChange={(e) => setFormData({ ...formData, payment_tsegment_id: e.target.value ? parseInt(e.target.value) : null })}
          >
            <option value="">None</option>
            {segmentsList.map(s => (
              <option key={s.tsegment_id} value={s.tsegment_id}>
                Segment: {s.carrier_iata || ''} {s.flight_number} {s.dep_iata} → {s.arr_iata}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="form-group">
        <label>Payment Initiator *</label>
        <select
          value={formData.payment_ctype || ''}
          onChange={(e) => {
            const initiator = e.target.value ? parseInt(e.target.value) : null;
            setFormData({
              ...formData,
              payment_ctype: initiator,
              payment_agent_id: initiator === 10 ? formData.payment_agent_id : null,
              payment_carrier_id: initiator === 20 ? formData.payment_carrier_id : null
            });
          }}
          required
        >
          <option value="">Select type</option>
          {Object.entries(paymentInitiators).map(([key, value]) => (
            <option key={key} value={key}>{value}</option>
          ))}
        </select>
      </div>

      {formData.payment_ctype === 10 && (
        <div className="form-group">
          <label>Agent</label>
          <select
            value={formData.payment_agent_id || ''}
            onChange={(e) => setFormData({ ...formData, payment_agent_id: e.target.value ? parseInt(e.target.value) : null })}
          >
            <option value="">Select agent</option>
            {agentsList.map(a => (
              <option key={a.agent_id} value={a.agent_id}>
                {a.agent_name}
              </option>
            ))}
          </select>
        </div>
      )}

      {formData.payment_ctype === 20 && (
        <div className="form-group">
          <label>Carrier</label>
          <select
            value={formData.payment_carrier_id || ''}
            onChange={(e) => setFormData({ ...formData, payment_carrier_id: e.target.value ? parseInt(e.target.value) : null })}
          >
            <option value="">Select carrier</option>
            {carriersList.map(c => (
              <option key={c.carrier_id} value={c.carrier_id}>
                {c.carrier_name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="form-group">
        <label>Payment Type *</label>
        <select
          value={formData.payment_type || ''}
          onChange={(e) => setFormData({ ...formData, payment_type: parseInt(e.target.value) })}
          required
        >
          <option value="">Select type</option>
          {Object.entries(paymentTypes).map(([key, value]) => (
            <option key={key} value={key}>{value}</option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Direction *</label>
        <select
          value={formData.payment_direction || ''}
          onChange={(e) => setFormData({ ...formData, payment_direction: parseInt(e.target.value) })}
          required
        >
          <option value="">Select direction</option>
          {Object.entries(paymentDirections).map(([key, value]) => (
            <option key={key} value={key}>{value}</option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Subject Type</label>
        <select
          value={formData.payment_subject_type || ''}
          onChange={(e) => {
            const subjectType = e.target.value ? parseInt(e.target.value) : null;
            setFormData({
              ...formData,
              payment_subject_type: subjectType,
              payment_subject_subtype: subjectType === 20 ? formData.payment_subject_subtype : null
            });
          }}
        >
          <option value="">Select subject type</option>
          {Object.entries(paymentSubjectTypes).map(([key, value]) => (
            <option key={key} value={key}>{value}</option>
          ))}
        </select>
      </div>

      {formData.payment_subject_type === 20 && (
        <div className="form-group">
          <label>Service Subtype</label>
          <select
            value={formData.payment_subject_subtype || ''}
            onChange={(e) => setFormData({ ...formData, payment_subject_subtype: e.target.value ? parseInt(e.target.value) : null })}
          >
            <option value="">Select service subtype</option>
            {Object.entries(paymentSubjectSubtypes).map(([key, value]) => (
              <option key={key} value={key}>{value}</option>
            ))}
          </select>
        </div>
      )}

      <div className="form-group">
        <label>Base Amount (in base currency) *</label>
        <input
          type="number"
          step="0.01"
          value={formData.payment_base_amt || ''}
          onChange={(e) => setFormData({ ...formData, payment_base_amt: parseFloat(e.target.value) })}
          required
        />
      </div>

      <div className="form-group">
        <label>Initial Payment Currency *</label>
        <select
          value={formData.payment_init_curr_id || ''}
          onChange={(e) => setFormData({ ...formData, payment_init_curr_id: parseInt(e.target.value) })}
          required
        >
          <option value="">Select currency</option>
          {currenciesList.map(c => (
            <option key={c.curr_id} value={c.curr_id}>
              {c.curr_symbol_code}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Initial Payment Amount *</label>
        <input
          type="number"
          step="0.01"
          value={formData.payment_init_amt || ''}
          onChange={(e) => setFormData({ ...formData, payment_init_amt: parseFloat(e.target.value) })}
          required
        />
      </div>

      <div className="form-group">
        <label>Account Operation Currency</label>
        <select
          value={formData.payment_acct_curr_id || ''}
          onChange={(e) => setFormData({ ...formData, payment_acct_curr_id: e.target.value ? parseInt(e.target.value) : null })}
        >
          <option value="">Select currency (optional)</option>
          {currenciesList.map(c => (
            <option key={c.curr_id} value={c.curr_id}>
              {c.curr_symbol_code}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Account Operation Amount</label>
        <input
          type="number"
          step="0.01"
          value={formData.payment_acct_amt || ''}
          onChange={(e) => setFormData({ ...formData, payment_acct_amt: e.target.value ? parseFloat(e.target.value) : null })}
        />
      </div>
    </>
  );
}

function PaymentsList() {
  return (
    <CRUDList
      title="Payments"
      api={payments}
      columns={['ID', 'Date', 'Booking', 'Ticket', 'Segment', 'Type', 'Direction', 'Payment Amount', 'Base Amount', 'Account Amount']}
      FormComponent={PaymentForm}
      defaultFormData={{
        payment_date: new Date().toISOString().split('T')[0],
        payment_ctype: 10,
        payment_agent_id: null,
        payment_carrier_id: null,
        payment_root_booking_id: '',
        payment_booking_id: null,
        ticket_booking_id: null,
        payment_ticket_id: null,
        payment_tsegment_id: null,
        payment_type: 10,
        payment_subject_type: null,
        payment_subject_subtype: null,
        payment_direction: 10,
        payment_base_amt: 0,
        payment_init_curr_id: '',
        payment_init_amt: 0,
        payment_acct_curr_id: null,
        payment_acct_amt: null
      }}
      useOldStyle={true}
      renderRow={(item) => (
        <>
          <td>{item.payment_id}</td>
          <td>{formatDate(item.payment_date)}</td>
          <td><strong>{item.booking_pnr}</strong></td>
          <td>{item.ticket_number || '-'}</td>
          <td>{item.seg_carrier_iata ? `${item.seg_carrier_iata}${item.seg_flight_number ? ` ${item.seg_flight_number}` : ''} ${item.seg_dep_iata} → ${item.seg_arr_iata}` : '-'}</td>
          <td><span className="badge badge-info">{paymentTypes[item.payment_type]}</span></td>
          <td><span className={`badge ${item.payment_direction === 10 ? 'badge-danger' : item.payment_direction === 20 ? 'badge-success' : 'badge-warning'}`}>
            {paymentDirections[item.payment_direction]}
          </span></td>
          <td>{formatAmount(item.payment_init_amt, item.init_curr_minor_units)} {item.init_curr_code}</td>
          <td>{formatAmount(item.payment_base_amt, item.base_curr_minor_units)} {item.base_curr_code}</td>
          <td>
            {item.payment_acct_amt !== null && item.acct_curr_code
              ? `${formatAmount(item.payment_acct_amt, item.acct_curr_minor_units)} ${item.acct_curr_code}`
              : '-'}
          </td>
        </>
      )}
    />
  );
}

export default PaymentsList;
